import React, { useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import Icon from '../components/Icon.js';
import WebTopBar from '../components/WebTopBar.js';
import { supabase } from '../../lib/supabase.js';

const TRUST_REPORT_NOTIFICATION_TYPES = new Set([
  'user_report_received',
  'user_report_resolved',
  'user_report_dismissed',
  'listing_report_received',
  'listing_report_resolved',
  'listing_report_dismissed',
  'message_report_received',
  'message_report_resolved',
  'message_report_dismissed',
]);

export default function PageNotificaciones({ onBack, onNavigate, user }) {
  const [filter, setFilter] = useState('Todas');
  const filters = ['Todas', 'Mensajes', 'Seguidores', 'Comunidad', 'Grooves', 'Reportes'];
  const [loading, setLoading] = useState(!!user?.id);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const [deletingById, setDeletingById] = useState({});
  const [deletingRead, setDeletingRead] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setRows([]);
      setLoading(false);
      setErr('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      const { data, error } = await supabase
        .from('notifications')
        .select('id,type,title,body,data,actor_id,is_read,created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(120);
      if (cancelled) return;
      if (error) {
        setRows([]);
        setErr(error.message || 'No se pudieron cargar las notificaciones.');
        setLoading(false);
        return;
      }
      setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const reload = async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id,type,title,body,data,actor_id,is_read,created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(120);
      if (!error) setRows(data ?? []);
    };
    const ch = supabase
      .channel(`notif-page:${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => {
          reload();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user?.id]);

  const unreadCount = useMemo(() => rows.filter(r => !r.is_read).length, [rows]);

  const filtered = useMemo(() => {
    if (filter === 'Todas') return rows;
    if (filter === 'Mensajes') return rows.filter(r => r.type === 'new_message');
    if (filter === 'Seguidores') return rows.filter(r => r.type === 'new_follower');
    if (filter === 'Comunidad')
      return rows.filter(
        r =>
          r.type === 'comunidad_publicacion_aprobada' ||
          r.type === 'comunidad_publicacion_rechazada' ||
          r.type === 'comunidad_portada_solicitud'
      );
    if (filter === 'Grooves')
      return rows.filter(r => r.type === 'grooves_ganados' || r.type === 'grooves_compra_confirmada');
    if (filter === 'Reportes') return rows.filter(r => r.type && TRUST_REPORT_NOTIFICATION_TYPES.has(r.type));
    return rows;
  }, [rows, filter]);

  const relTime = iso => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const diff = Math.max(0, Date.now() - d.getTime());
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs} h`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const uiForType = type => {
    if (type === 'new_message') return { icon: 'chat', ic: 'purple' };
    if (type === 'new_follower') return { icon: 'user', ic: 'green' };
    if (type === 'comunidad_publicacion_aprobada') return { icon: 'check', ic: 'green' };
    if (type === 'comunidad_publicacion_rechazada') return { icon: 'info', ic: 'gold' };
    if (type === 'comunidad_portada_solicitud') return { icon: 'leisure', ic: 'gold' };
    if (type === 'grooves_ganados' || type === 'grooves_compra_confirmada') return { icon: 'star', ic: 'gold' };
    if (type === 'partner_solicitud_aprobada') return { icon: 'check', ic: 'green' };
    if (type === 'partner_solicitud_rechazada') return { icon: 'info', ic: 'gold' };
    if (type && TRUST_REPORT_NOTIFICATION_TYPES.has(type))
      return { icon: 'info', ic: type.endsWith('_dismissed') ? 'gray' : 'gold' };
    return { icon: 'bell', ic: 'gray' };
  };

  const icColors = { gold: C.gold, green: C.green, purple: C.purpleL, gray: 'rgba(255,255,255,0.4)' };
  const icBgs = { gold: 'rgba(200,168,75,0.15)', green: 'rgba(29,158,117,0.15)', purple: 'rgba(83,74,183,0.15)', gray: 'rgba(255,255,255,0.06)' };

  const markAllRead = async () => {
    if (!user?.id || unreadCount === 0 || markingAll) return;
    setMarkingAll(true);
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);
    setMarkingAll(false);
    if (error) {
      setErr(error.message || 'No se pudieron marcar como leídas.');
      return;
    }
    setRows(prev => prev.map(x => ({ ...x, is_read: true })));
  };

  const markOneRead = async id => {
    if (!user?.id || !id) return;
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id)
      .eq('user_id', user.id);
    if (!error) setRows(prev => prev.map(x => (x.id === id ? { ...x, is_read: true } : x)));
  };

  const deleteNotif = async id => {
    if (!user?.id || !id) return;
    setDeletingById(p => ({ ...p, [id]: true }));
    const { error } = await supabase.from('notifications').delete().eq('id', id).eq('user_id', user.id);
    setDeletingById(p => ({ ...p, [id]: false }));
    if (error) {
      setErr(error.message || 'No se pudo borrar la notificación.');
      return;
    }
    setRows(prev => prev.filter(x => x.id !== id));
  };

  const deleteRead = async () => {
    if (!user?.id || deletingRead) return;
    if (!window.confirm('¿Borrar todas las notificaciones ya leídas?')) return;
    setDeletingRead(true);
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id)
      .eq('is_read', true);
    setDeletingRead(false);
    if (error) {
      setErr(error.message || 'No se pudieron borrar las notificaciones leídas.');
      return;
    }
    setRows(prev => prev.filter(x => !x.is_read));
  };

  const openNotif = async n => {
    if (!n?.id) return;
    if (!n.is_read) await markOneRead(n.id);

    if (n.type === 'new_message') {
      const cid = n.data?.conversation_id;
      if (!cid) return;
      // Resolver vendedor/otro usuario para que el header del chat se vea bien
      const { data: conv } = await supabase
        .from('conversations')
        .select('id,buyer_id,seller_id,listing_id')
        .eq('id', cid)
        .maybeSingle();
      if (!conv) {
        onNavigate?.('chat', { conversationId: cid });
        return;
      }
      const otherId = conv.buyer_id === user?.id ? conv.seller_id : conv.buyer_id;
      const { data: prof } = otherId
        ? await supabase.from('profiles').select('id,username,avatar_url').eq('id', otherId).maybeSingle()
        : { data: null };

      onNavigate?.('chat', {
        conversationId: cid,
        sellerUsername: prof?.username ?? null,
        sellerUserId: otherId ?? null,
        sellerAvatarUrl: prof?.avatar_url ?? null,
        listingId: conv.listing_id ?? null,
      });
      return;
    }

    if (n.type === 'new_follower') {
      const actorId = n.actor_id;
      if (!actorId) return;
      const { data: prof } = await supabase.from('profiles').select('id,username').eq('id', actorId).maybeSingle();
      if (!prof?.username) return;
      onNavigate?.('perfil-vendedor', { userId: prof.id, username: prof.username });
      return;
    }

    if (n.type === 'comunidad_publicacion_aprobada' || n.type === 'comunidad_publicacion_rechazada') {
      onNavigate?.('comunidad');
      return;
    }

    if (n.type === 'grooves_ganados' || n.type === 'grooves_compra_confirmada') {
      onNavigate?.('perfil');
      return;
    }

    if (
      n.type === 'marketplace_compra_confirmada' ||
      n.type === 'partner_custodia_estado' ||
      n.type === 'partner_custodia_listo'
    ) {
      const txId = n.data?.transaction_id;
      if (txId) onNavigate?.('pedido', { highlightTransactionId: txId });
      else onNavigate?.('pedido');
      return;
    }

    if (n.type === 'marketplace_venta_nueva') {
      onNavigate?.('mis-ventas');
      return;
    }

    if (n.type === 'partner_custodia_nueva' || n.type === 'subasta_pendiente_partner') {
      onNavigate?.('partner-panel');
      return;
    }

    if (n.type === 'subasta_activada' || n.type === 'subasta_solicitud_registrada') {
      const lid = n.data?.listing_id;
      if (lid) onNavigate?.('ficha', { id: lid, isAuction: true });
      else onNavigate?.('mis-ventas');
      return;
    }

    if (n.type === 'partner_solicitud_aprobada') {
      onNavigate?.('partner-panel');
      return;
    }

    if (n.type === 'partner_solicitud_rechazada') {
      onNavigate?.('mis-solicitudes');
      return;
    }

    if (n.type === 'comunidad_portada_solicitud') {
      onNavigate?.('admin', { adminActive: 'comunidad', adminComunidadTab: 'feed' });
      return;
    }
  };

  return (
    <div style={{ background:C.dark, minHeight:'100vh', color:'#fff' }}>
      <WebTopBar onBack={onBack} title="Notificaciones" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
      <div style={{ maxWidth:900, margin:'0 auto', padding:'14px 20px 60px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <button
            onClick={deleteRead}
            disabled={deletingRead || rows.filter(r => r.is_read).length === 0}
            style={{ background:'none', border:'none', color:'rgba(255,255,255,0.55)', fontSize:11, cursor: deletingRead ? 'wait' : 'pointer', opacity: deletingRead || rows.filter(r => r.is_read).length === 0 ? 0.35 : 1 }}
          >
            {deletingRead ? 'Borrando…' : 'Borrar leídas'}
          </button>
          <button
            onClick={markAllRead}
            disabled={unreadCount === 0 || markingAll}
            style={{ background:'none', border:'none', color:C.gold, fontSize:11, cursor: unreadCount === 0 || markingAll ? 'default' : 'pointer', opacity: unreadCount === 0 || markingAll ? 0.45 : 1 }}
          >
            {markingAll ? 'Marcando…' : 'Marcar todo leído'}
          </button>
        </div>

        <div style={{ display:'flex', gap:8, padding:'0 0 10px', overflowX:'auto' }}>
          {filters.map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{ padding:'5px 13px', borderRadius:20, fontSize:11, whiteSpace:'nowrap', cursor:'pointer', border:`0.5px solid ${filter===f?C.gold:'rgba(255,255,255,0.15)'}`, color:filter===f?C.gold:'rgba(255,255,255,0.6)', background:filter===f?'rgba(200,168,75,0.15)':'transparent', flexShrink:0 }}>
              {f}
            </button>
          ))}
        </div>

        {err ? (
          <div style={{ padding: '14px 0', fontSize: 12, color: '#f0a8a8' }}>{err}</div>
        ) : loading ? (
          <div style={{ padding: '14px 0', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Cargando notificaciones…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '18px 0', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>No hay notificaciones en esta vista.</div>
        ) : (
          <>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', textTransform:'uppercase', letterSpacing:'0.5px', padding:'12px 0 6px' }}>
              {unreadCount > 0 ? `${unreadCount} sin leer` : 'Todo al día'}
            </div>
            {filtered.map(n => {
              const ui = uiForType(n.type);
              const text =
                n.type === 'grooves_compra_confirmada' && n.title
                  ? [n.title, n.body].filter(Boolean).join(' — ')
                  : n.body || n.title || 'Notificación';
              return (
                <div
                  key={n.id}
                  onClick={() => openNotif(n)}
                  style={{
                    display:'flex',
                    gap:12,
                    padding:'12px 0',
                    borderBottom:'0.5px solid rgba(255,255,255,0.05)',
                    background:!n.is_read?'rgba(200,168,75,0.02)':'transparent',
                    cursor:'pointer'
                  }}
                >
                  <div style={{ width:36, height:36, borderRadius:'50%', background:icBgs[ui.ic], display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                    <Icon name={ui.icon} size={16} color={icColors[ui.ic]}/>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', lineHeight:1.4 }}>{text}</div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', marginTop:3 }}>{relTime(n.created_at)}</div>
                  </div>
                  <button
                    type="button"
                    title="Borrar"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteNotif(n.id);
                    }}
                    disabled={!!deletingById[n.id]}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: deletingById[n.id] ? 'wait' : 'pointer',
                      color: 'rgba(255,255,255,0.35)',
                      padding: '6px 6px',
                      alignSelf: 'flex-start',
                      opacity: deletingById[n.id] ? 0.5 : 1,
                    }}
                  >
                    <Icon name="x" size={14} color="rgba(255,255,255,0.35)" />
                  </button>
                  {!n.is_read && <div style={{ width:7, height:7, borderRadius:'50%', background:C.gold, flexShrink:0, marginTop:6 }}/>}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}